import { figure } from "@cliqsuite/ui";
import { useMemo, useState } from "react";
import { useAdminData } from "../state/AdminDataContext";
import { useIsDesktop } from "../hooks/useIsDesktop";
import type { Payment, PaymentStatus } from "../lib/adminTypes";
import { avColor, chipStyle, initials, methodMeta, mmk, statusMeta, waitHours, waitLabel } from "../lib/format";
import { PaymentDetailDrawer } from "../components/PaymentDetailDrawer";
import { RejectModal } from "../components/RejectModal";

type StatusFilter = "all" | PaymentStatus;
type MethodFilter = "all" | "qr" | "bank" | "wallet";
type DateFilter = "all" | "today" | "7d" | "30d";
type SortKey = "newest" | "oldest" | "amount";

const PER_PAGE = 7;

export function HomePage() {
  const { payments, approve, reject, reopen } = useAdminData();
  const isDesktop = useIsDesktop();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [methodFilter, setMethodFilter] = useState<MethodFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [sort, setSort] = useState<SortKey>("oldest");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);

  const subs = payments;
  const byStatus = useMemo(
    () => ({
      pending: subs.filter((p) => p.status === "pending"),
      approved: subs.filter((p) => p.status === "approved"),
      rejected: subs.filter((p) => p.status === "rejected"),
    }),
    [subs],
  );
  const revenue = byStatus.approved.reduce((s, p) => s + p.amount, 0);
  const now = useMemo(() => new Date(), []);
  const waitHrs = (p: Payment) => waitHours(p.submittedAt, now);
  const breaches = byStatus.pending.filter((p) => waitHrs(p) > 24);
  const oldestWait = byStatus.pending.length ? Math.max(...byStatus.pending.map(waitHrs)) : 0;

  const pillDefs: Array<[StatusFilter, string, number]> = [
    ["pending", "Pending", byStatus.pending.length],
    ["approved", "Approved", byStatus.approved.length],
    ["rejected", "Rejected", byStatus.rejected.length],
    ["all", "All", subs.length],
  ];

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    let list = subs.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (methodFilter !== "all" && p.method !== methodFilter) return false;
      if (dateFilter !== "all") {
        const days = dateFilter === "today" ? 1 : dateFilter === "7d" ? 7 : 30;
        const diff = (now.getTime() - new Date(p.submittedAt).getTime()) / 86400000;
        if (diff > days) return false;
      }
      if (qq) {
        const hay = `${p.ref} ${p.business} ${p.businessType} ${p.billingEmail} ${p.txid || ""} ${p.planName}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) =>
      sort === "amount" ? b.amount - a.amount : sort === "oldest" ? new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime() : new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime(),
    );
    return list;
  }, [subs, q, statusFilter, methodFilter, dateFilter, sort, now]);

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, pages);
  const start = (currentPage - 1) * PER_PAGE;
  const pageRows = filtered.slice(start, start + PER_PAGE);

  const detailPayment = detailId ? payments.find((p) => p.id === detailId) ?? null : null;
  const rejectPayment = rejectId ? payments.find((p) => p.id === rejectId) ?? null : null;

  const stat = (icon: string, label: string, value: string, dark = false) => (
    <div className={`o-stat${dark ? " is-dark" : ""}`}>
      <div className="o-stat__label">
        <span className="ms" aria-hidden="true">{icon}</span>
        {label}
      </div>
      <div className="o-stat__value num">{figure(value)}</div>
    </div>
  );
  const approvedThisMonth = byStatus.approved.filter((p) => {
    const d = new Date(p.submittedAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div>
      <div className="o-kpis">
        {stat("pending_actions", "Pending review", String(byStatus.pending.length), true)}
        {stat("hourglass_bottom", "Oldest waiting", byStatus.pending.length ? `${Math.round(oldestWait)} h` : "—")}
        {stat("verified", "Approved this month", String(approvedThisMonth))}
        {stat("block", "Rejected", String(byStatus.rejected.length))}
        {stat("payments", "Verified revenue", mmk(revenue))}
      </div>

      {breaches.length > 0 && (
        <div className="o-banner">
          <span className="ms" aria-hidden="true">timer</span>
          <span style={{ flex: 1 }}>
            {breaches.length} {breaches.length === 1 ? "payment has" : "payments have"} waited over 24 hours
          </span>
          <button
            type="button"
            className="o-banner__btn"
            onClick={() => {
              setStatusFilter("pending");
              setSort("oldest");
              setPage(1);
            }}
          >
            Oldest first
          </button>
        </div>
      )}

      <div className="o-toolbar">
        <label className="o-search o-search--line" style={{ flex: 1, width: "auto", minWidth: 200 }}>
          <span className="ms" aria-hidden="true">search</span>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search ref, user, business, TXID"
            aria-label="Search"
          />
        </label>
        <div className="o-chips" role="tablist" style={{ flex: "0 0 auto" }}>
          {pillDefs.map(([key, label, count]) => (
            <button
              type="button"
              role="tab"
              aria-checked={statusFilter === key}
              aria-selected={statusFilter === key}
              key={key}
              onClick={() => {
                setStatusFilter(key);
                setPage(1);
              }}
            >
              {label}
              {key === "pending" && count > 0 && <span className="num" style={{ opacity: 0.55, marginLeft: 7 }}>{count}</span>}
            </button>
          ))}
        </div>
        <select
          className="o-select"
          value={methodFilter}
          onChange={(e) => {
            setMethodFilter(e.target.value as MethodFilter);
            setPage(1);
          }}
          aria-label="Method"
        >
          <option value="all">All methods</option>
          <option value="qr">QR payment</option>
          <option value="bank">Bank transfer</option>
          <option value="wallet">Crypto wallet</option>
        </select>
        <select
          className="o-select"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value as DateFilter);
            setPage(1);
          }}
          aria-label="Date"
        >
          <option value="all">Any date</option>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
        <select className="o-select" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort">
          <option value="oldest">Oldest first</option>
          <option value="newest">Newest first</option>
          <option value="amount">Highest amount</option>
        </select>
      </div>

      <section className="o-card">
        {pageRows.length === 0 && (
          <div className="o-empty">
            <span className="ms" aria-hidden="true">inbox</span>
            <strong>{statusFilter === "pending" && !q ? "Nothing waiting" : "No payments match"}</strong>
            <p>{statusFilter === "pending" && !q ? "New payment proofs show up here." : "Try other filters."}</p>
          </div>
        )}
        {pageRows.length > 0 && (
          <div className="o-table" style={{ ["--cols" as string]: "0.9fr 1.5fr 1.1fr 1.1fr 1fr 0.8fr 1.1fr 36px" }}>
            <div className="o-table__head">
              <span>Payment</span>
              <span>Business</span>
              <span>Plan</span>
              <span>Method</span>
              <span>Amount</span>
              <span>Waiting</span>
              <span>Status</span>
              <span />
            </div>
            {pageRows.map((p) => {
              const mm = methodMeta(p.method);
              const sm = statusMeta(p.status);
              const h = waitHrs(p);
              return (
                <button type="button" key={p.id} className="o-table__row" onClick={() => setDetailId(p.id)} aria-label={`Open ${p.ref}`}>
                  <span className="num" style={{ fontWeight: 800 }}>{p.ref}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="o-avatar" style={{ width: 34, height: 34, borderRadius: 11, background: avColor(p.business), fontSize: 12 }}>{initials(p.business)}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis" }}>{p.business}</span>
                      <span className="o-row__sub">{p.businessType}</span>
                    </span>
                  </span>
                  <span>
                    <span style={{ display: "block" }}>{p.planName}</span>
                    <span className="o-row__sub">{p.cycleLabel}</span>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span className="ms" aria-hidden="true" style={{ fontSize: 18, color: mm.color }}>{mm.icon}</span>
                    {p.methodLabel}
                  </span>
                  <span className="num" style={{ fontWeight: 800 }}>{mmk(p.amount)}</span>
                  <span className="num" style={{ color: p.status === "pending" && h > 24 ? "var(--coral)" : "var(--text2)" }}>
                    {p.status === "pending" ? waitLabel(h) : p.dateLabel.replace(/, \d{4}$/, "")}
                  </span>
                  <span>
                    <span className="o-pill" style={chipStyle(sm)}>{p.resubmitted && p.status === "pending" ? "Re-submitted" : sm.label}</span>
                  </span>
                  <span className="o-icon-btn" aria-hidden="true"><span className="ms">chevron_right</span></span>
                </button>
              );
            })}
          </div>
        )}
        {filtered.length > PER_PAGE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, paddingTop: 14, flexWrap: "wrap" }}>
            <div className="o-row__sub">
              {start + 1} to {Math.min(start + PER_PAGE, filtered.length)} of {filtered.length}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="o-icon-btn" aria-label="Previous page" onClick={() => setPage((x) => Math.max(1, x - 1))} disabled={currentPage <= 1}>
                <span className="ms" aria-hidden="true">chevron_left</span>
              </button>
              <button type="button" className="o-icon-btn" aria-label="Next page" onClick={() => setPage((x) => Math.min(pages, x + 1))} disabled={currentPage >= pages}>
                <span className="ms" aria-hidden="true">chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </section>

      {detailPayment && (
        <PaymentDetailDrawer
          payment={detailPayment}
          onClose={() => setDetailId(null)}
          onApprove={() => {
            approve(detailPayment.id);
            setDetailId(null);
          }}
          onReject={() => setRejectId(detailPayment.id)}
          onResubmit={() => {
            reopen(detailPayment.id);
            setDetailId(null);
          }}
        />
      )}
      {rejectPayment && (
        <RejectModal
          payment={rejectPayment}
          onClose={() => setRejectId(null)}
          onConfirm={(reason) => {
            reject(rejectPayment.id, reason);
            setRejectId(null);
            setDetailId(null);
          }}
        />
      )}
    </div>
  );
}
